import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuthContext } from '../../auth/AuthContext';
import { submitFeedback } from '../../service/form';
import { resolveSelectedFormId } from '../../utils/form-id';
import { getTenantFromPath } from '../../utils/tenant-utils';

const FEEDBACK_THANK_YOU_COUNTDOWN_SECONDS = 2;
type ShareEmailAnswer = '' | 'yes' | 'no';

function PhaseBanner() {
  const { fetchJWT } = useAuthContext();
  const location = useLocation();
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const shareEmailSelectRef = useRef<HTMLSelectElement>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [feedback, setFeedback] = useState('');
  const [feedbackError, setFeedbackError] = useState('');
  const [shareEmail, setShareEmail] = useState<ShareEmailAnswer>('');
  const [shareEmailError, setShareEmailError] = useState('');
  const [submitError, setSubmitError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [countdown, setCountdown] = useState<number | null>(null);
  const [openedUrl, setOpenedUrl] = useState('');
  const [openedFormId, setOpenedFormId] = useState<string | undefined>();

  const tenant = useMemo(
    () => getTenantFromPath(location.pathname || window.location.pathname),
    [location.pathname],
  );
  const activeFormId = useMemo(
    () => resolveSelectedFormId(tenant, location.search || ''),
    [location.search, tenant],
  );

  useEffect(() => {
    if (!isOpen || countdown !== null) return;
    shareEmailSelectRef.current?.focus();
  }, [countdown, isOpen]);

  useEffect(() => {
    if (countdown === null) return;

    const timer = window.setTimeout(() => {
      if (countdown <= 1) {
        setIsOpen(false);
        setCountdown(null);
        setFeedback('');
        setFeedbackError('');
        setShareEmail('');
        setShareEmailError('');
        setSubmitError('');
        setOpenedUrl('');
        setOpenedFormId(undefined);
        return;
      }
      setCountdown((current) => (current === null ? current : current - 1));
    }, 1000);

    return () => {
      window.clearTimeout(timer);
    };
  }, [countdown]);

  const openDialog = () => {
    setIsOpen(true);
    setFeedback('');
    setFeedbackError('');
    setShareEmail('');
    setShareEmailError('');
    setSubmitError('');
    setIsSubmitting(false);
    setCountdown(null);
    setOpenedUrl(window.location.href);
    setOpenedFormId(activeFormId);
  };

  const closeDialog = () => {
    setIsOpen(false);
    setFeedback('');
    setFeedbackError('');
    setShareEmail('');
    setShareEmailError('');
    setSubmitError('');
    setIsSubmitting(false);
    setCountdown(null);
  };

  const handleSubmit = async () => {
    const trimmedFeedback = feedback.trim();
    const nextFeedbackError = trimmedFeedback ? '' : 'Enter your feedback';
    const nextShareEmailError = shareEmail
      ? ''
      : 'Select whether to share your email with the form owner';

    setFeedbackError(nextFeedbackError);
    setShareEmailError(nextShareEmailError);
    setSubmitError('');

    if (nextFeedbackError || nextShareEmailError) {
      return;
    }

    setIsSubmitting(true);

    try {
      const tokens = await fetchJWT({ redirectOnFail: false });
      if (!tokens.idToken) {
        setSubmitError('You must be signed in to submit feedback.');
        return;
      }

      await submitFeedback(tokens.idToken, {
        feedback: trimmedFeedback,
        current_url: openedUrl || window.location.href,
        ...(openedFormId ? { form_id: openedFormId } : {}),
        share_email: shareEmail === 'yes',
      });
      setCountdown(FEEDBACK_THANK_YOU_COUNTDOWN_SECONDS);
    } catch (error) {
      console.error('Failed to submit feedback', error);
      setSubmitError('Unable to submit feedback right now.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <section className="govuk-phase-banner" aria-label="Phase banner">
        <p className="govuk-phase-banner__content">
          <strong
            className="govuk-tag govuk-phase-banner__content__tag"
            style={{ backgroundColor: '#1d70b8', color: '#ffffff', fontWeight: 'bold' }}
            aria-label="Beta phase"
          >
            BETA
          </strong>
          <span className="govuk-phase-banner__text">
            This is a new service. Your{' '}
            <button
              type="button"
              className="govuk-link phase-banner-feedback-link"
              onClick={openDialog}
              aria-label="Provide feedback on this new service"
            >
              feedback
            </button>{' '}
            will help us to improve it.
          </span>
        </p>
      </section>

      {isOpen && (
        <div className="feedback-modal-backdrop">
          <div
            className="feedback-modal govuk-!-padding-6"
            role="dialog"
            aria-modal="true"
            aria-labelledby="feedback-modal-title"
          >
            {countdown === null ? (
              <>
                <h2 id="feedback-modal-title" className="govuk-heading-m govuk-!-margin-bottom-4">
                  Please enter your feedback below
                </h2>
                <div
                  className={`govuk-form-group${
                    shareEmailError ? ' govuk-form-group--error' : ''
                  }`}
                >
                  <label className="govuk-label" htmlFor="feedback-share-email">
                    Share your email with the form owner?
                  </label>
                  {shareEmailError && (
                    <p id="feedback-share-email-error" className="govuk-error-message" role="alert">
                      <span className="govuk-visually-hidden">Error:</span> {shareEmailError}
                    </p>
                  )}
                  <select
                    ref={shareEmailSelectRef}
                    className={`govuk-select${shareEmailError ? ' govuk-select--error' : ''}`}
                    id="feedback-share-email"
                    name="share-email"
                    value={shareEmail}
                    required
                    aria-describedby={
                      shareEmailError ? 'feedback-share-email-error' : undefined
                    }
                    onChange={(event) => {
                      setShareEmail(event.target.value as ShareEmailAnswer);
                      if (shareEmailError) {
                        setShareEmailError('');
                      }
                      if (submitError) {
                        setSubmitError('');
                      }
                    }}
                  >
                    <option value="">Select an option</option>
                    <option value="yes">Yes</option>
                    <option value="no">No</option>
                  </select>
                </div>
                <div
                  className={`govuk-form-group${
                    feedbackError || submitError ? ' govuk-form-group--error' : ''
                  }`}
                >
                  {(feedbackError || submitError) && (
                    <p className="govuk-error-message" role="alert">
                      <span className="govuk-visually-hidden">Error:</span>{' '}
                      {feedbackError || submitError}
                    </p>
                  )}
                  <textarea
                    ref={textareaRef}
                    className="govuk-textarea"
                    id="feedback-textarea"
                    name="feedback"
                    rows={6}
                    value={feedback}
                    onChange={(event) => {
                      setFeedback(event.target.value);
                      if (feedbackError) {
                        setFeedbackError('');
                      }
                      if (submitError) {
                        setSubmitError('');
                      }
                    }}
                  />
                </div>
                <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                  <button
                    type="button"
                    className="govuk-button"
                    data-module="govuk-button"
                    onClick={() => {
                      void handleSubmit();
                    }}
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? 'Submitting...' : 'Submit'}
                  </button>
                  <button
                    type="button"
                    className="govuk-button govuk-button--secondary"
                    data-module="govuk-button"
                    onClick={closeDialog}
                    disabled={isSubmitting}
                  >
                    Cancel
                  </button>
                </div>
              </>
            ) : (
              <p
                id="feedback-modal-title"
                className="govuk-heading-m govuk-!-margin-bottom-0"
                aria-live="polite"
              >
                Thank you for your feedback ({countdown})
              </p>
            )}
          </div>
        </div>
      )}
    </>
  );
}

export default PhaseBanner;
